import { useCallback, useState, useEffect, useContext, useMemo } from "react";
import { AppContext } from "../../../Contexts/Contexts";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { MenuList } from "@mui/material";
import DomUtils from "../../../utils/dom.utils";
import { copy } from "../../../utils/general.utils";
import IdUtils from "../../../utils/id.utils";
import {
  DataGridPro,
  useGridApiRef,
  gridClasses,
  GridCellEditStopReasons,
} from "@mui/x-data-grid-pro";

const ExtractionTable = ({ table, onChange, onFocus, onRowUpdate }) => {
  const { confirm } = useContext(AppContext);
  const apiRef = useGridApiRef();
  const [cellModified, setCellModified] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  //GetSetters
  const getSetApiRef = useCallback((apiRef) => (table.ref = apiRef), [table]);

  const getRowData = useCallback(() => {
    const rowData = copy(table.rows);
    rowData.map((row) => {
      for (const cell in row) {
        if (cell !== "id" && cell !== "position") {
          row[cell] = row[cell].data;
        }
      }
      return { ...row };
    });
    return rowData;
  }, [table]);

  const columns = useMemo(() => {
    const columnConfig = table.config.columns
      .filter((column) => column.visibility !== "collapsed")
      .map((column) => {
        return {
          field: column.name,
          headerName: column.label,
          type: column.inputType === "autocomplete" ? "singleSelect" : "string",
          valueOptions: column?.options,
          editable: true,
          preProcessEditCellProps: (params) => {
            setCellModified(params.hasChanged);
          },
          renderCell: (params) => {
            const field = table.rows.find((row) => row.id === params.id);
            return (
              <div
                style={{
                  position: "relative",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    width: "85%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {params.value}
                </div>
                {DomUtils.getValidatedIcon(field[params.field])}
              </div>
            );
          },
        };
      });
    return columnConfig;
  }, [table]);

  //Actions
  const processRowUpdate = useCallback(
    (updatedRow) => {
      if (cellModified) {
        onChange(table.name, updatedRow);
      }
      setCellModified(false);
      return updatedRow;
    },
    [cellModified, onChange, table]
  );

  const createNewCell = (columnName, lowThresh) => {
    return {
      columnName: columnName,
      conf: lowThresh,
      threshold: lowThresh,
      confOriginal: 0,
      data: "",
      dataOriginal: "",
      position: {
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
      },
    };
  };

  const addRow = useCallback(
    (rowPlacement) => {
      const newRowData = {
        id: IdUtils.generateUuid(),
        position: {
          page: 0,
          inputFilePageNumber: 0,
          inputFileID: 0,
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
        },
      };
      for (const column of table.config.columns) {
        newRowData[column.name] = createNewCell(
          column.name,
          table.config.threshold
        );
      }
      const rowIndex = table.rows.findIndex((row) => row.id === selectedRow);
      switch (rowPlacement) {
        case "above":
          table.rows.splice(rowIndex, 0, newRowData);
          break;
        case "below":
          table.rows.splice(rowIndex + 1, 0, newRowData);
          break;
        default:
          table.rows.push(newRowData);
          break;
      }
      onRowUpdate(table.name, "add");
    },
    [table, onRowUpdate, selectedRow]
  );

  const deleteSelectedRows = useCallback(() => {
    const rowIndex = table.rows.findIndex((row) => row.id === selectedRow);
    apiRef.current.updateRows([{ id: selectedRow, _action: "delete" }]);
    table.rows.splice(rowIndex, 1);
    onRowUpdate(table.name, "delete");
  }, [onRowUpdate, table, apiRef, selectedRow]);

  //Clicks
  const onCellEditStart = useCallback(
    async (params, e) => {
      if (
        params.reason === GridCellEditStopReasons.enterKeyDown ||
        params.reason === GridCellEditStopReasons.tabKeyDown
      ) {
        e.defaultMuiPrevented = true;
        return;
      }
      const row = table.rows.find((row) => row.id === params.id);
      const rowIdx = table.rows.findIndex((row) => row.id === params.id);
      const colIdx = table.columnNames.findIndex(
        (column) => column === params.field
      );
      const fieldData = {
        tableName: table.name,
        rowId: params.id,
        idx: { ...table.idx, tableRow: rowIdx, tableCol: colIdx },
        ...row[params.field],
      };
      await onFocus(fieldData);
    },
    [onFocus, table]
  );

  const onCellEditStop = useCallback(
    (params, e) => {
      e.defaultMuiPrevented = true;
      apiRef.current.stopCellEditMode({
        id: params.id,
        field: params.field,
      });
      if (
        !cellModified &&
        params.reason === GridCellEditStopReasons.enterKeyDown
      ) {
        setCellModified(!cellModified);
      }
    },
    [apiRef, cellModified]
  );

  const onMenuOpen = useCallback(
    (event) => {
      event.preventDefault();
      const dataId = event.currentTarget.getAttribute("data-id");
      apiRef.current.selectRow(dataId);
      setSelectedRow(dataId);
      setContextMenu(
        contextMenu === null
          ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
          : null
      );
    },
    [contextMenu, apiRef]
  );

  const onMenuClose = useCallback(() => {
    apiRef.current.selectRow(selectedRow, false);
    setSelectedRow(null);
    setContextMenu(null);
  }, [apiRef, selectedRow]);

  const onAddRow = useCallback(
    (rowPlacement) => {
      onMenuClose();
      addRow(rowPlacement);
    },
    [addRow, onMenuClose]
  );

  const onRowDelete = useCallback(() => {
    onMenuClose();
    confirm.open(
      <div>Are you sure you want to delete the selected row?</div>,
      () => deleteSelectedRows()
    );
  }, [confirm, deleteSelectedRows, onMenuClose]);

  //useEffects
  useEffect(() => {
    getSetApiRef(apiRef);
  }, [apiRef, getSetApiRef]);

  //Sub-components
  const NoRowsOverlay = () => {
    return (
      <div
        className="no-row-overlay"
        onContextMenu={onMenuOpen}
        style={{ cursor: "context-menu" }}
      >
        <div>No Rows</div>
        {UpdateRowMenu(table.rows)}
      </div>
    );
  };

  const UpdateRowMenu = (rows) => {
    return (
      <Menu
        open={contextMenu !== null}
        onClose={onMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          root: {
            onContextMenu: (e) => {
              e.preventDefault();
              onMenuClose();
            },
          },
        }}
      >
        {rows.length > 0 ? (
          <MenuList>
            <MenuItem onClick={() => onAddRow("above")}>Add Row Above</MenuItem>
            <MenuItem onClick={() => onAddRow("below")}>Add Row Below</MenuItem>
            <MenuItem onClick={onRowDelete}>Delete Row</MenuItem>
          </MenuList>
        ) : (
          <MenuItem onClick={() => onAddRow()}>Add Row</MenuItem>
        )}
      </Menu>
    );
  };

  return (
    <div
      style={{
        position: "relative",
        margin: "6.5px",
        minWidth: "100px",
      }}
    >
      <DataGridPro
        apiRef={apiRef}
        autosizeOnMount
        autosizeOptions={{
          columns: columns.map((col) => col.field),
          includeHeaders: true,
          expand: true,
        }}
        columns={columns}
        disableColumnMenu
        hideFooterSelectedRowCount
        onCellEditStart={onCellEditStart}
        onCellEditStop={onCellEditStop}
        pagination
        processRowUpdate={processRowUpdate}
        rows={getRowData()}
        slotProps={{
          row: {
            onContextMenu: onMenuOpen,
            style: { cursor: "context-menu" },
          },
        }}
        slots={{
          noRowsOverlay: NoRowsOverlay,
        }}
        sx={gridSx}
      />
      {UpdateRowMenu(table.rows)}
    </div>
  );
};

export default ExtractionTable;

const gridSx = {
  ".MuiDataGrid-columnHeaders, .MuiDataGrid-pinnedColumnHeaders": {
    backgroundColor: "var(--true-black)",
    color: "white",
    fontFamily: "var(--font-family-sans-serif)",
    borderRadius: "0px",
  },
  ".MuiDataGrid-pinnedColumns": {
    boxShadow: "none",
  },
  [`.MuiDataGrid-sortIcon, .MuiDataGrid-menuIconButton, .${gridClasses.filterIcon}`]:
    {
      color: "var(--true-green)",
    },
  ".MuiDataGrid-cell": {
    fontFamily: "var(--font-family-serif)",
  },
  ".MuiDataGrid-row.Mui-selected.Mui-hovered": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  "& .MuiDataGrid-virtualScroller": {
    backgroundColor: "white",
    minHeight: "52px",
  },
  [`.${gridClasses.cell}:focus, .${gridClasses.cell}:focus-within, .${gridClasses.columnHeader}:focus, .${gridClasses.columnHeader}:focus-within`]:
    {
      outline: "none",
    },
};
